import { Injectable, computed, inject, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { BehaviorSubject, Observable, filter } from "rxjs";

import { ApiSessionService } from "../../api-session.service";
import {
  Organization,
  ApiOrganization,
  ApiOrganizationToOrganization as clean,
  EditCallerIdParams,
  OrganizationAddParams,
  OrgMfaParams
} from "./organization";
import { ApiAction } from "../../api-actions";
import { ParameterValue } from "../../util/api-action-description";
import { extractData } from "../../apiResponse/response-body-new";
import { genericApiAction, IApiError } from "../../onsip-api-action-new";
import { arrayToRecord } from "../../util/arrayToRecord";
import { ResourceState, initialResourceState } from "../../resource-state";
import { Contact } from "../../apiResponse/contact";
export { Organization };

const countryID = 207; // USA

@Injectable({ providedIn: "root" })
export class OrganizationService {
  protected readonly session = inject(ApiSessionService);

  static readonly organizationsById = new BehaviorSubject<Record<string, Organization>>({});

  private readonly _state =
    signal<ResourceState<Organization>>(initialResourceState<Organization>());

  constructor() {
    toObservable(this._state).subscribe(s => OrganizationService.organizationsById.next(s.data));
  }

  readonly state = this._state.asReadonly();
  readonly organizations = computed((): Array<Organization> => Object.values(this._state().data));

  readonly selfOrganization = computed(
    (): Organization | undefined => this._state().data[this.session.organizationId() || ""]
  );

  private readonly _orgOrganizations = computed((): Array<Organization> =>
    this.organizations().filter(o => o.organizationId === this.session.organizationId())
  );

  private readonly _accountOrganizations = computed((): Array<Organization> =>
    this.organizations().filter(o => o.accountId === this.session.accountId())
  );

  private readonly selfOrganization$ = toObservable(this.selfOrganization).pipe(filter(Boolean));
  private readonly orgOrganizations$ = toObservable(this._orgOrganizations);
  private readonly accountOrganizations$ = toObservable(this._accountOrganizations);

  get organization(): Observable<Organization> {
    return this.selfOrganization$;
  }

  get orgState(): Observable<Array<Organization>> {
    return this.orgOrganizations$;
  }

  get accountState(): Observable<Array<Organization>> {
    return this.accountOrganizations$;
  }

  dispose(): void {
    this._state.set(initialResourceState<Organization>());
  }

  async organizationBrowse(params?: Record<string, ParameterValue>): Promise<void> {
    this._state.update(s => ({ ...s, loading: true }));
    try {
      const response = await genericApiAction({
        Action: ApiAction.OrganizationBrowse,
        AccountId: this.session.accountId(),
        SessionId: this.session.sessionId(),
        Limit: 100,
        ...params
      });
      const orgs = extractData<Array<ApiOrganization>>(
        response,
        ApiAction.OrganizationBrowse,
        "Organization",
        "Organizations"
      ).map(clean);
      this._state.update(s => ({
        ...s,
        data: arrayToRecord(orgs, "organizationId"),
        loading: false,
        loaded: true
      }));
    } catch (e: unknown) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as IApiError] }));
      throw e;
    }
  }

  async organizationAdd(params: OrganizationAddParams): Promise<Organization> {
    const response = await genericApiAction({
      Action: ApiAction.OrganizationAdd,
      AccountId: this.session.accountId(),
      SessionId: this.session.sessionId(),
      UserRole: "User",
      ...params
    });
    const org = clean(
      extractData<ApiOrganization>(response, ApiAction.OrganizationAdd, "Organization")
    );
    this._state.update(s => ({
      ...s,
      data: { ...s.data, [org.organizationId]: org }
    }));
    return org;
  }

  async organizationRead(extraParameters?: Record<string, ParameterValue>): Promise<Organization> {
    this._state.update(s => ({ ...s, loading: true }));
    try {
      const response = await genericApiAction({
        Action: ApiAction.OrganizationRead,
        SessionId: this.session.sessionId(),
        ...extraParameters
      });
      const org = clean(
        extractData<ApiOrganization>(response, ApiAction.OrganizationRead, "Organization")
      );
      this._state.update(s => ({
        ...s,
        data: { ...s.data, [org.organizationId]: org },
        loading: false,
        loaded: true
      }));
      return org;
    } catch (e: unknown) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as IApiError] }));
      throw e;
    }
  }

  async organizationMigrate(params: {
    OldDomain: string;
    NewDomain: string;
  }): Promise<Organization> {
    const response = await genericApiAction({
      Action: ApiAction.OrganizationMigrateDomain,
      SessionId: this.session.sessionId(),
      OrganizationId: this.session.organizationId(),
      UserId: this.session.userId(),
      ...params
    });
    const org = clean(
      extractData<ApiOrganization>(response, ApiAction.OrganizationMigrateDomain, "Organization")
    );
    this._state.update(s => ({
      ...s,
      data: { ...s.data, [org.organizationId]: org }
    }));
    return org;
  }

  async organizationEdit911Provisioning(params: {
    Enabled: boolean;
    Cascade: boolean;
    OrganizationId?: string;
  }): Promise<Organization> {
    const response = await genericApiAction({
      Action: ApiAction.OrganizationEditE911Provisioning,
      SessionId: this.session.sessionId(),
      OrganizationId: this.session.organizationId(),
      ...params
    });
    const org = clean(
      extractData<ApiOrganization>(
        response,
        ApiAction.OrganizationEditE911Provisioning,
        "Organization"
      )
    );
    this._state.update(s => ({
      ...s,
      data: { ...s.data, [org.organizationId]: org }
    }));
    return org;
  }

  async organizationEditBusyLampField(Enabled: boolean): Promise<Organization> {
    const response = await genericApiAction({
      Action: ApiAction.OrganizationEditBusyLampField,
      SessionId: this.session.sessionId(),
      OrganizationId: this.session.organizationId(),
      Enabled
    });
    const org = clean(
      extractData<ApiOrganization>(
        response,
        ApiAction.OrganizationEditBusyLampField,
        "Organization"
      )
    );
    this._state.update(s => ({
      ...s,
      data: { ...s.data, [org.organizationId]: org }
    }));
    return org;
  }

  async organizationEditCallerId(params: EditCallerIdParams): Promise<Organization> {
    const response = await genericApiAction({
      Action: ApiAction.OrganizationEditCallerId,
      SessionId: this.session.sessionId(),
      OrganizationId: this.session.organizationId(),
      ...params
    });
    const org = clean(
      extractData<ApiOrganization>(response, ApiAction.OrganizationEditCallerId, "Organization")
    );
    this._state.update(s => ({
      ...s,
      data: { ...s.data, [org.organizationId]: org }
    }));
    return org;
  }

  async organizationEditContact(params: Contact): Promise<Organization> {
    const contact = {
      Name: params.name,
      Address: params.address,
      City: params.city,
      State: params.state,
      Zipcode: params.zipcode,
      CountryId: countryID,
      Phone: params.phone,
      Email: params.email,
      Organization: params.organization
    };
    const response = await genericApiAction({
      Action: ApiAction.OrganizationEditContact,
      SessionId: this.session.sessionId(),
      OrganizationId: this.session.organizationId(),
      ...contact
    });
    const org = clean(
      extractData<ApiOrganization>(response, ApiAction.OrganizationEditContact, "Organization")
    );
    this._state.update(s => ({
      ...s,
      data: { ...s.data, [org.organizationId]: org }
    }));
    return org;
  }

  async organizationEditMfaEmail(params: OrgMfaParams): Promise<Organization> {
    const response = await genericApiAction({
      Action: ApiAction.OrganizationEditMfaEmail,
      SessionId: this.session.sessionId(),
      OrganizationId: this.session.organizationId(),
      ...params
    });
    const org = clean(
      extractData<ApiOrganization>(response, ApiAction.OrganizationEditMfaEmail, "Organization")
    );
    this._state.update(s => ({
      ...s,
      data: { ...s.data, [org.organizationId]: org }
    }));
    return org;
  }

  async organizationEditMessaging(Enabled: boolean): Promise<Organization> {
    const response = await genericApiAction({
      Action: ApiAction.OrganizationEditMessaging,
      SessionId: this.session.sessionId(),
      OrganizationId: this.session.organizationId(),
      Enabled
    });
    const org = clean(
      extractData<ApiOrganization>(response, ApiAction.OrganizationEditMessaging, "Organization")
    );
    this._state.update(s => ({
      ...s,
      data: { ...s.data, [org.organizationId]: org }
    }));
    return org;
  }
}
